"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  CreditCard,
  Settings,
  Shield,
  Plus,
  List,
  Calendar,
  TrendingUp,
  Bell,
  Tag,
  Download,
  LucideIcon
} from 'lucide-react';
import type { Tenant } from '@vm/theme';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

interface SidebarProps {
  tenant: Tenant;
  user?: {
    name?: string;
    email?: string;
    role?: 'user' | 'admin';
    tier?: string;
  };
  navItems?: NavItem[];
  onCreateNew?: () => void;
  onUpgrade?: () => void;
  showCreateButton?: boolean;
  showUpgradeCard?: boolean;
  createButtonLabel?: string;
}

const defaultNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'My Subscriptions', href: '/subscriptions', icon: List },
  { label: 'Calendar', href: '/calendar', icon: Calendar },
  { label: 'Trends', href: '/trends', icon: TrendingUp },
  { label: 'Reminders', href: '/reminders', icon: Bell },
  { label: 'Tags', href: '/tags', icon: Tag },
  { label: 'Export', href: '/export', icon: Download },
  { label: 'Billing', href: '/billing', icon: CreditCard },
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Admin', href: '/admin', icon: Shield, adminOnly: true },
];

export function Sidebar({
  tenant,
  user,
  navItems: customNavItems,
  onCreateNew,
  onUpgrade,
  showCreateButton = true,
  showUpgradeCard = true,
  createButtonLabel = 'Create New'
}: SidebarProps) {
  const pathname = usePathname();

  // Keyboard shortcut for Create New (Cmd+N / Ctrl+N)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        onCreateNew?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCreateNew]);

  // Use custom nav items or default, filter based on user role
  const baseNavItems = customNavItems || defaultNavItems;
  const navItems = baseNavItems.filter(item => {
    if (item.adminOnly) {
      return user?.role === 'admin';
    }
    return true;
  });

  return (
    <aside className="w-64 flex flex-col gap-6 border-r border-white/10 bg-slate-900/50 backdrop-blur-lg p-6 h-screen sticky top-0">
      {/* Logo - Clickable to Dashboard */}
      <Link href="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer">
        <Image
          src="/logo.png"
          alt={`${tenant.name} Logo`}
          width={32}
          height={32}
          className="h-8 w-8 rounded-lg border border-white/10 bg-white/5 p-1"
        />
        <span className="text-3xl font-semibold tracking-tight text-white">{tenant.name}</span>
      </Link>

      {/* Primary Action Button */}
      {showCreateButton && onCreateNew && (
        <button
          type="button"
          onClick={onCreateNew}
          className="btn-gradient-dynamic flex items-center justify-between gap-3 rounded-lg px-4 py-3 text-sm font-semibold text-white transition-all"
          title={`${createButtonLabel} (⌘N)`}
        >
          <span className="flex items-center gap-3">
            <Plus className="h-4 w-4" />
            {createButtonLabel}
          </span>
          <kbd className="text-xs text-white/60 hidden sm:block">⌘N</kbd>
        </button>
      )}

      {/* Navigation */}
      <nav className="flex flex-col gap-1 text-sm">
        {navItems.map((item) => {
          const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
          
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition ${
                isActive 
                  ? 'bg-white/10' 
                  : 'hover:bg-white/10'
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Upgrade Card / PRO Member Card */}
      {showUpgradeCard && (
      <div className="mt-auto rounded-xl border border-white/10 bg-slate-900/60 p-4">
        {user?.tier?.toLowerCase() === 'pro' ? (
          // PRO Member Card
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-white/60">
                PRO Member
              </span>
              <span className="rounded-full bg-gradient-to-r from-orange-400 to-pink-400 px-2 py-0.5 text-xs font-bold text-white">
                PRO
              </span>
            </div>
            <div className="space-y-1.5 text-xs text-white/70">
              <div className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <span>Unlimited subscriptions</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <span>Advanced analytics</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <span>Priority support</span>
              </div>
            </div>
            <Link
              href="/billing"
              className="block w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-center text-xs font-medium text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              Manage Billing
            </Link>
          </div>
        ) : (
          // Free Tier - Upgrade Button
          <button
            type="button"
            onClick={onUpgrade}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-400 to-pink-400 px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Upgrade to Pro
          </button>
        )}
      </div>
      )}
    </aside>
  );
}
